import React, { useEffect, useState } from "react"
import { Divider } from "antd"
import { Row, Col } from "antd"
import {
  useSetPolicyRenewals,
  useGetPolicyRenewals,
  useSetPolicyRenewalsWithdraw
} from "../../../../services/hooks"
import { TPolicyRenewalsSettings } from "models/policies/policyRenewals"
import { useConfigurationContext } from "contexts/configuration/context"
import { PolicyRenewals } from "../../v2_components"
import { withRouter, RouteComponentProps } from "react-router-dom"
import { TPolicyRenewalsBundle } from "models/policies/policyRenewals"
import PolicyRenewalQuotes from "../../v2_components/policy-renewal-quotes"
import useGetPolicyRenewalQuotes from "containers/policies/services/hooks/use-get-policy-renewal-quote"
import { useSetPolicyRenewalsReinstate } from "containers/policies/services/hooks/use-set-policy-renewal-reinstate"

export const PolicyRenewalsComponent: React.FunctionComponent<IPolicyRenewalsSubView> =
  (props) => {
    const configuration = useConfigurationContext()

    const [policyRenewalsData, setPolicyRenewalsData] = useState<
      TPolicyRenewalsBundle | undefined
    >()

    const {
      error,
      isLoading,
      isFirstLoad,
      renewalQuotes,
      setAssignedQuote,
      duplicateRenewalQuote,
      reloadQuotes
    } = useGetPolicyRenewalQuotes(props?.policyID)

    const {
      isSending: isRenewalsWorking,
      sendRequest: sendRenewalsRequest,
      isError: isRenewalsError,
      renewals: renewalsUpdated
    } = useSetPolicyRenewals(props?.policyID)

    const { policyRenewals, isPolicyRenewalsLoading } = useGetPolicyRenewals(
      props.policyID
    )

    const {
      sendRequest: setPolicyRenewalWithdraw,
      response: policyWithdrawResponse
    } = useSetPolicyRenewalsWithdraw(props.policyID, reloadQuotes)

    const {
      sendRequest: setPolicyRenewalReinstated,
      response: policyReinstatedResponse
    } = useSetPolicyRenewalsReinstate(props.policyID, reloadQuotes)

    useEffect(() => {
      if (policyRenewals && !policyWithdrawResponse) {
        setPolicyRenewalsData(policyRenewals)
      }
      if (renewalsUpdated) {
        setPolicyRenewalsData(renewalsUpdated)
      }
      if (policyWithdrawResponse) {
        setPolicyRenewalsData(policyWithdrawResponse)
      }
    }, [policyRenewals, renewalsUpdated, policyWithdrawResponse])

    useEffect(() => {
      if (policyRenewals && !policyReinstatedResponse) {
        setPolicyRenewalsData(policyRenewals)
      }
      if (renewalsUpdated) {
        setPolicyRenewalsData(renewalsUpdated)
      }
      if (policyReinstatedResponse) {
        setPolicyRenewalsData(policyReinstatedResponse)
      }
    }, [policyRenewals, renewalsUpdated, policyReinstatedResponse])

    const isPolicyRenewalsWithdrawn = policyRenewalsData?.status === "Withdrawn"

    return (
      <>
        <Row gutter={24}>
          <Col span={24}>
            {props.policyRenewalsSettings?.supportsRenewals &&
              policyRenewalsData &&
              configuration.policy?.details && (
                <PolicyRenewals
                  renewalsSettings={props.policyRenewalsSettings.paymentPlans}
                  renewalsConfiguration={configuration.policy?.details}
                  setPolicyRenewalWithdraw={setPolicyRenewalWithdraw}
                  setPolicyRenewalReinstated={setPolicyRenewalReinstated}
                  isPolicyRenewalsWithdrawn={isPolicyRenewalsWithdrawn}
                  policyPlanReferenceID={props.policyPlanReferenceID}
                  isError={isRenewalsError}
                  sendRequest={sendRenewalsRequest}
                  isSending={isRenewalsWorking}
                  isLoading={isPolicyRenewalsLoading}
                  values={{
                    requiresReview: policyRenewalsData.requiresReview,
                    allowAutoBind: policyRenewalsData.allowAutoBind,
                    doNotRenew: policyRenewalsData.doNotRenew
                  }}
                  refetchTimelineEvents={props.refetchTimelineEvents}
                />
              )}
          </Col>
        </Row>
        <Divider />
        <Row gutter={24}>
          <Col span={24}>
            {props.policyRenewalsSettings?.supportsRenewals && (
              <PolicyRenewalQuotes
                policyId={props.policyID}
                isPolicyCancelled={props.isPolicyCancelled}
                isPolicyRenewalsWithdrawed={isPolicyRenewalsWithdrawn}
                noIntentionToRenew={policyRenewalsData?.doNotRenew}
                isError={error}
                isLoading={isLoading}
                isFirstLoad={isFirstLoad}
                renewalQuotes={renewalQuotes}
                setAssignedQuote={setAssignedQuote}
                duplicateRenewalQuote={duplicateRenewalQuote}
                reloadQuotes={reloadQuotes}
                policyStartDate={props.policyStartDate}
                policyEndDate={props.policyEndDate}
              />
            )}
          </Col>
        </Row>
      </>
    )
  }

export const PolicyRenewalsSubView = withRouter(PolicyRenewalsComponent)

export interface IPolicyRenewalsSubView extends RouteComponentProps {
  isPolicyCancelled: boolean
  policyID: string
  policyPlanReferenceID: string | undefined
  policyProductID: number | undefined
  policyRenewalsSettings: TPolicyRenewalsSettings | undefined
  refetchTimelineEvents: VoidFunction
  policyStartDate?: string
  policyEndDate?: string
}
